import React from 'react';
import * as mobxReact from 'mobx-react';
import { injectIntl, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import './filter-pills.scss';
import DefaultButton from '../../../../global/default-button/default-button';
import { FilterField, IConfigurationFilter, ISelectableFilter, } from '../../../../../api/reports.dao';
import FilterPill from '../filter-pill/filter-pill';
import ReportsViewModel from '../../_reports/reports.viewmodel';
import DateFilterPill from '../date-filter-pill/date-filter-pill';
import { IMeetingDetailsDTO } from '../../../../../api/meetings.dao';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';

export interface IProps {
    intl: IntlShape;
    vm: ReportsViewModel;
    filters: IConfigurationFilter[];
    clearAll: () => void;
    clear: (filter: ISelectableFilter) => void;
    select: (filter: ISelectableFilter) => void;
    selected?: ISelectableFilter;
    availableFilterTypes: ISelectableFilter[];
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);
const MAX_MEETINGS_PILL_COUNT = 4;

const FilterPills = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        intl,
        vm,
        filters,
        clearAll,
        clear,
        select,
        selected,
        availableFilterTypes
    } = props;

    const { meetingsFilterVM } = vm;
    const handleClearAll = () => {
        vm.clearDates();
        clearAll();
    };


    const getFilterPill = (filter: IConfigurationFilter) => {
        switch (filter.field) {
            case FilterField.MEETING_DATE:
                return <DateFilterPill
                        key={filter.field}
                        filter={filter}
                        select={select}
                        clear={vm.clearDates}
                        isSelected={selected?.field === filter.field}
                        firstSelectedDate={vm.firstSelectedDate}
                        lastSelectedDate={vm.lastSelectedDate}
                        availableDates={vm.getAvailableDates()}
                        availableFilterTypes={availableFilterTypes}
                    />;
            case FilterField.MEETING_ID:
                const selectedMeetings: IMeetingDetailsDTO[] = meetingsFilterVM.getSelectedMeetings(filter.values);
                let pillLabels = [];
                selectedMeetings.filter(meeting => {
                    const formattedStartDateTime = dateTimeFormatter.dateToFormattedString(
                        meeting.startDateTime,
                        DateFnsFormats.StringifiedFullDayMonthNumericDateYear
                    );
                    let label = '';
                    label += formattedStartDateTime;
                    meeting.presenters.filter(presenter => label += ', ' + presenter.name);
                    pillLabels.push(label);
                });

                const customText = <div title={pillLabels.join('\n')}>
                    {pillLabels.slice(0, MAX_MEETINGS_PILL_COUNT).map(value =>
                        <div className='overflow-ellipsis filter-pills__meeting-pill'>{value}</div>
                    )}
                    {pillLabels.length > MAX_MEETINGS_PILL_COUNT && (
                        <span className='filter-pills__count'>
                            {`+${pillLabels.length - MAX_MEETINGS_PILL_COUNT}`}
                        </span>
                    )}
                </div>;

                return <FilterPill
                    key={filter.field}
                    filter={filter}
                    select={select}
                    clear={clear}
                    isSelected={selected?.field === filter.field}
                    customText={customText}
                    availableFilterTypes={availableFilterTypes}
                />;
            default:
                return <FilterPill
                    key={filter.field}
                    filter={filter}
                    select={select}
                    clear={clear}
                    isSelected={selected?.field === filter.field}
                    availableFilterTypes={availableFilterTypes}
                />;
        }

    };
    return (
        <div className='filter-pills'>
            { !!filters.length &&
                <DefaultButton
                    modifier='link'
                    className='filter-pills__clear'
                    text={intl.formatMessage(
                        {
                            id: 'oe.eventTracker.clearAllFilters',
                            description: 'Clear filters button',
                            defaultMessage: 'Clear All'
                        }
                    )}
                    onClick={handleClearAll}
                />
            }
            <div className='filter-pills__items'>
                { filters.map(filter => getFilterPill(filter))}
            </div>
        </div>
    );
}));

export default FilterPills;
